// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;
  const windowOrigin =
    typeof window !== 'undefined' && window.location?.origin ? window.location.origin : null;
  const envRootUrl = (process.env.REACT_APP_MARKETPLACE_ROOT_URL || '').replace(/\/$/, '');

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    if (process.env.REACT_APP_DEV_API_SERVER_PROXY === 'true') {
      return marketplaceRootURL
        ? marketplaceRootURL.replace(/\/$/, '')
        : windowOrigin || envRootUrl || `http://localhost:${port}`;
    }
    const devApiServerUrl = (process.env.REACT_APP_DEV_API_SERVER_URL || '')
      .trim()
      .replace(/\/+$/, '');
    if (devApiServerUrl) {
      return devApiServerUrl;
    }
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : windowOrigin || envRootUrl;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set or explicitly transit, serialize the payload for Marketplace/SDK.
  // Other Content-Types pass `body` through (e.g. pre-stringified application/json).
  const contentType = headers?.['Content-Type'];
  const useTransitSerialization =
    (!headers || contentType === 'application/transit+json') && body != null;

  const bodyMaybe = useTransitSerialization
    ? { body: serialize(body) }
    : body != null
    ? {
        body: typeof body === 'string' ? body : JSON.stringify(body),
      }
    : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  // globalThis.fetch works in the browser and on modern Node (SSR loadData).
  const fetchFn = typeof window !== 'undefined' ? window.fetch.bind(window) : globalThis.fetch;
  if (typeof fetchFn !== 'function') {
    return Promise.reject(new Error('fetch is not available to call the marketplace API server'));
  }

  return fetchFn(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    // Some JSON endpoints intentionally return empty bodies with 204 No Content (e.g. idempotent submits).
    if (res.status === 204 || res.status === 205) {
      return null;
    }

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Multi-listing box checkout. Server prices the box, reserves each listing on a
// child transaction, then initiates the payment transaction.
export const initiateCartPrivileged = body => {
  return post('/api/initiate-cart-privileged', body);
};

export const acceptCartStock = body => {
  return request('/api/accept-cart-stock', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body || {}),
    credentials: 'include',
  });
};

export const sellerCancelOrder = body => {
  return request('/api/seller-cancel-order', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body || {}),
    credentials: 'include',
  });
};

export const stampOrderNumber = body => {
  return post('/api/stamp-order-number', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace. Server injects managed password for OAuth-only users.
export const deleteUserAccount = (body = {}) => {
  return post('/api/delete-account', body);
};

// Change current user's email. Server injects managed password (OAuth-only).
export const changeUserEmail = body => {
  return post('/api/change-email', body);
};

/** Switch Marketplace `publicData.userType` via authenticated server endpoint. */
export const switchMarketplaceAccountUserType = ({ targetUserType }) => {
  return request('/api/account/user-type', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ targetUserType }),
    credentials: 'include',
  });
};

/** Fetch account types the current user may switch to (R2R group-gated). */
export const fetchSwitchableAccountUserTypes = () => {
  return request('/api/account/switchable-user-types', {
    method: methods.GET,
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });
};

export const refreshXfUserGroups = () => {
  return request('/api/account/refresh-xf-groups', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({}),
    credentials: 'include',
  });
};

export const syncMarketplaceGroups = () => {
  return request('/api/account/sync-marketplace-groups', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({}),
    credentials: 'include',
  }).catch(() => ({ ok: false, changed: false }));
};

/**
 * Search published listings via Integration API (honors createdAtStart).
 * Body must include createdAtStart. See server/api/search-listings.js.
 */
export const searchListingsViaIntegration = body => {
  return post('/api/search-listings', body);
};

/**
 * Active Diamond Sponsor logos for the site footer (AMS package 49 via server proxy).
 * @returns {Promise<{ sponsors: Array<{ imageUrl: string, targetUrl?: string }> }>}
 */
export const fetchDiamondSponsors = () => {
  return request('/api/diamond-sponsors', {
    method: methods.GET,
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });
};

/**
 * Revalidate cart rows against live listing data and get the server-computed shipping
 * ledger for each box. The cart persists only the minimal row shape, so everything the
 * cart renders — and all money — comes from here.
 *
 * @param {Array<{ listingId: string, qty: number, deliveryMethod: string, unitPriceAtAdd: number|null }>} rows
 * @param {{ destination?: { country?: string, state?: string } }} [options]
 * @returns {Promise<{ rows: Array<Object>, groups: Array<Object> }>}
 */
export const cartRevalidate = (rows, options = {}) => {
  const destination = options?.destination;
  const body = { rows: Array.isArray(rows) ? rows : [] };
  if (destination && (destination.country || destination.state)) {
    body.destination = {
      ...(destination.country ? { country: String(destination.country) } : {}),
      ...(destination.state ? { state: String(destination.state) } : {}),
    };
  }
  return request('/api/cart-revalidate', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  });
};

/**
 * Unread XenForo DM count for the logged-in marketplace user.
 * @returns {Promise<{ unread: number }>}
 */
export const fetchForumUnreadConversations = () => {
  return request('/api/forum/unread-conversations', {
    method: methods.GET,
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });
};

/**
 * Typeahead over active marketplace sellers (published listings).
 * @param {string} query
 * @returns {Promise<{ sellers: Array<{ id: string, username: string|null, displayName: string|null, avatarUrl: string|null, listingCount: number }> }>}
 */
export const fetchSellerSuggestions = query => {
  const q = typeof query === 'string' ? query : '';
  return request(`/api/sellers/suggest?q=${encodeURIComponent(q)}`, {
    method: methods.GET,
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });
};

/**
 * Resolve `?seller=<forumUsername>` to a compact seller record.
 * @param {string} username
 * @returns {Promise<{ seller: object|null }>}
 */
export const fetchSellerByUsername = username => {
  const value = typeof username === 'string' ? username : '';
  return request(`/api/sellers/resolve?username=${encodeURIComponent(value)}`, {
    method: methods.GET,
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });
};

export const fetchSellerDirectory = () => {
  return request('/api/sellers/directory', {
    method: methods.GET,
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });
};

/**
 * Subscribe an email address to the Reef2Reef Constant Contact list.
 * @param {{ email: string, b_address?: string }} body b_address is a honeypot
 * @returns {Promise<{ ok: true }>}
 */
export const subscribeToNewsletter = ({ email, b_address = '' }) => {
  return request('/api/newsletter-signup', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ email, b_address }),
    credentials: 'include',
  });
};

/**
 * Upload a listing video/GIF for transcoding and CDN storage.
 *
 * This bypasses `request()` deliberately: the body is FormData, so the browser must set the
 * multipart Content-Type (with its boundary) rather than the transit default.
 *
 * @param {File} file
 * @returns {Promise<Object>} { id, mp4Key, cardMp4Key, posterKey, width, height, durationSec, createdAt }
 */
export const uploadListingVideo = async file => {
  const formData = new FormData();
  formData.append('video', file);

  const response = await globalThis.fetch(`${apiBaseUrl()}/api/upload-listing-video`, {
    method: methods.POST,
    credentials: 'include',
    body: formData,
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new Error(data?.message || 'Video upload failed');
    error.code = data?.code || 'video-upload-failed';
    error.status = response.status;
    throw error;
  }

  return data;
};

/**
 * Parse a listing-import CSV on the server (session cookie).
 * @param {FormData} formData
 */
export const parseListingImportCsv = formData => {
  return globalThis
    .fetch(`${apiBaseUrl()}/api/listing-import/parse`, {
      method: methods.POST,
      credentials: 'include',
      body: formData,
    })
    .then(async res => {
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        const error = new Error(data?.message || 'Listing import parse failed');
        error.code = data?.code || 'import-parse-failed';
        error.column = data?.column;
        error.status = res.status;
        throw error;
      }
      return data;
    });
};

/**
 * Create one imported draft from a mapped row.
 * @param {object} body
 */
export const createListingImportRow = body => {
  return request('/api/listing-import/create', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body || {}),
    credentials: 'include',
  });
};
